import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars, elevationCss } from '@creditornot/cb-ingredients';
import { ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import * as api from 'apis/restaurant-api/merchant-admin/venue/api';
import { APIErrorNotification, WoltModal } from 'common/components';
import { Typography as T } from 'common/typography';

import { stripMerchantAppLoginPrefix } from '../utils';

type Props = {
  isOpen: boolean;
  venueId: string;
  login: string;
  onClose: () => void;
};

const Root = styled(WoltModal)`
  width: 100%;
  max-width: 640px;
`;

const Container = styled.div`
  ${elevationCss(16)}
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const Main = styled.div`
  padding: 32px;
`;

const Password = styled.div`
  width: 100%;
  margin-block-start: 20px;
  padding: 8px;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 4px;
  text-align: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
  font-family: Menlo, Monaco, 'Courier New', monospace;
`;

const Error = styled(APIErrorNotification)`
  margin-block-start: 20px;
`;

const CancelButton = styled(Button).attrs({
  variant: 'secondary',
  size: 'small',
})``;

const ResetButton = styled(Button).attrs({
  variant: 'secondaryNegative',
  size: 'small',
})``;

const DoneButton = styled(Button).attrs({
  variant: 'primary',
  size: 'small',
})``;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;

  ${ResetButton} {
    margin-inline-start: 16px;
  }
`;

type NewPasswordProps = {
  login: string;
  newPassword: string;
  onClose: () => void;
};

const NewPassword = ({ login, newPassword, onClose }: NewPasswordProps) => {
  return (
    <>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="userManagement.passwordHasBeenReset" />
      </ModalHeader>
      <Main>
        <T.Body2>
          <FormattedMessage
            id="userManagement.passwordHasBeenResetDescription"
            values={{
              strong: chunks => <strong>{chunks}</strong>,
              username: stripMerchantAppLoginPrefix(login),
            }}
          />
        </T.Body2>
        <Password>{newPassword}</Password>
      </Main>
      <StyledModalFooter>
        <DoneButton onClick={onClose}>
          <FormattedMessage id="userManagement.done" />
        </DoneButton>
      </StyledModalFooter>
    </>
  );
};

type PasswordResetProps = {
  error: Error | null;
  loading: boolean;
  login: string;
  onClose: () => void;
  onReset: () => void;
};

const PasswordReset = ({ error, loading, login, onClose, onReset }: PasswordResetProps) => {
  return (
    <>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="userManagement.resetPasswordTitle" />
      </ModalHeader>
      <Main>
        <T.Body2>
          <FormattedMessage
            id="userManagement.resetPasswordDescription"
            values={{
              strong: chunks => <strong>{chunks}</strong>,
              username: stripMerchantAppLoginPrefix(login),
            }}
          />
        </T.Body2>
        {error && <Error error={error} />}
      </Main>
      <StyledModalFooter>
        <CancelButton disabled={loading} onClick={onClose}>
          <FormattedMessage id="userManagement.cancel" />
        </CancelButton>
        <ResetButton
          loading={loading}
          onClick={() => {
            onReset();
          }}
        >
          <FormattedMessage id="userManagement.resetPasswordConfirmation" />
        </ResetButton>
      </StyledModalFooter>
    </>
  );
};

const PasswordResetModal = ({ isOpen, venueId, login, onClose }: Props) => {
  const passwordResetMutation = useMutation<string, Error>(async () => {
    const { password } = await api.resetMerchantAppLoginPassword(venueId, login);
    return password;
  });

  const handleNewPasswordClose = () => {
    onClose();
    passwordResetMutation.reset();
  };

  return (
    <Root isOpen={isOpen} onRequestClose={onClose}>
      <Container>
        {passwordResetMutation.data ? (
          <NewPassword login={login} newPassword={passwordResetMutation.data} onClose={handleNewPasswordClose} />
        ) : (
          <PasswordReset
            error={passwordResetMutation.error}
            loading={passwordResetMutation.isLoading}
            login={login}
            onClose={onClose}
            onReset={passwordResetMutation.mutate}
          />
        )}
      </Container>
    </Root>
  );
};

export default PasswordResetModal;
